import * as React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { CheckoutOrderFragment, GetAccountInfoQuery } from '@websky/graphql';
import { Step } from '../../../types';
import { getIsBooked, getOrder } from '../../../store/order/selectors';
import PassengersToolbar from '../Form/PassengersToolbar/PassengersToolbar';
import Inner from './Inner/Inner';
import { EngineTheme as EveryTravelEngine } from '@websky/every-travel/src/theme';
import { SearchFormTheme } from '@websky/every-travel/src/Widget/CostaBrava/CostaBrava';
import { WidgetCart } from '@websky/every-travel/src/types';
import { EngineTheme, ThemeProvider, useTheme } from '../../../../theme';
import { OwnProps } from '../routes/Order';
import Consents from '../../../../Consents';
import getOrderDataLayer from '../../../../DataLayerAnalytics/orderDataLayerInstance';
import WebskyServicesAnalytics from '../../../../DataLayerAnalytics/WebskyServicesAnalytics';
import { scrollTo } from '../../../../utils';
import { useQueryControlledServicesModal } from '../hooks';
import { useSetLayoutMode } from '@shared/lib/layout-mode';

interface Props {
	currentStep: Step;
	setNextStep: () => void;
	userInfo?: GetAccountInfoQuery['CurrentUser'];
	refetchUser?: () => Promise<GetAccountInfoQuery>;
	goToRefund: React.DispatchWithoutAction;
	goToExchange: React.DispatchWithoutAction;
	goToChangePassenger: OwnProps['goToChangePassenger'];
	everyTravelStyles?: EveryTravelEngine;
	everyTravelSearchFormStyles?: SearchFormTheme;
}

const ServicesAggregationPage: React.FC<Props> = props => {
	const setLayoutMode = useSetLayoutMode();
	const [segmentId, setSegmentId] = React.useState('0');
	const segmentIdRef = React.useRef(segmentId);

	const order: CheckoutOrderFragment = useSelector(getOrder) as CheckoutOrderFragment;
	const isBooked = useSelector(getIsBooked);
	const nextSegmentScroll = React.useRef(false);

	const theme = useTheme('Checkout').ServicesAggregationPage;

	const [everyTravelCart, setEveryTravelCart] = React.useState<WidgetCart>(null);
	const [needToConfirmEveryTravelCart, setNeedToConfirmEveryTravelCart] = React.useState(false);

	useQueryControlledServicesModal();

	const onScrollListener = () => {
		if (nextSegmentScroll.current && window.pageYOffset <= 0) {
			nextSegmentScroll.current = false;

			setTimeout(setNextSegment, 300);
		}
	};

	useEffect(() => {
		scrollTo(0);
		setLayoutMode('aggregator');
	}, [setLayoutMode]);

	useEffect(() => {
		if (isBooked) {
			getOrderDataLayer().beginCheckout(WebskyServicesAnalytics.getCheckoutSelectedServices(order));
		}
	}, [isBooked]);

	useEffect(() => {
		window.addEventListener('scroll', onScrollListener);

		return () => {
			window.removeEventListener('scroll', onScrollListener);
		};
	});

	const changeSegmentWithSmooth = () => {
		if (window.pageYOffset > 0) {
			nextSegmentScroll.current = true;

			setTimeout(() => {
				scrollTo(0);
			}, 0);
		} else {
			setNextSegment();
		}
	};

	const setNextSegment = () => {
		setSegmentId(segmentId => {
			if (+segmentId < order.flight.segments.length - 1) {
				return (+segmentId + 1).toString();
			}

			return segmentId;
		});
	};

	const onNextClickHandler = () => {
		const segmentIdInt = parseInt(segmentIdRef.current);

		if (segmentIdInt < order.flight.segments.length - 1) {
			changeSegmentWithSmooth();
		} else {
			if (!everyTravelCart?.isOrdered && everyTravelCart?.items?.length > 0) {
				setNeedToConfirmEveryTravelCart(true);
			} else {
				props.setNextStep();
			}
		}
	};

	React.useEffect(() => {
		segmentIdRef.current = segmentId;
	}, [segmentId]);

	React.useEffect(() => {
		if (needToConfirmEveryTravelCart && (everyTravelCart.isOrdered || !everyTravelCart?.items?.length)) {
			setNeedToConfirmEveryTravelCart(false);

			props.setNextStep();
		}
	}, [everyTravelCart]);

	const aggregatorStyles: EngineTheme = {
		ServiceSummary: {
			ServiceSummaryDivider: {
				container: theme.serviceSummaryDivider
			}
		},
		CheckoutAdditionalService: {
			AggregatorService: {
				container: theme.aggregatorService__container
			}
		}
	};

	return (
		<div>
			<ThemeProvider value={aggregatorStyles}>
				<Inner
					segmentId={segmentId}
					onNextSegment={onNextClickHandler}
					setSegmentId={setSegmentId}
					userInfo={props.userInfo}
					refetchUser={props.refetchUser}
					goToChangePassenger={props.goToChangePassenger}
					goToRefund={props.goToRefund}
					goToExchange={props.goToExchange}
					everyTravelStyles={props.everyTravelStyles}
					everyTravelSearchFormStyles={props.everyTravelSearchFormStyles}
					everyTravelCart={everyTravelCart}
					setEveryTravelCart={setEveryTravelCart}
					needToConfirmCart={needToConfirmEveryTravelCart}
				/>
			</ThemeProvider>

			<Consents>
				{({ onConfirm }) => (
					<PassengersToolbar
						currentStep={props.currentStep}
						onNextClick={onNextClickHandler}
						setNextStep={onNextClickHandler}
						onConfirmConsents={onConfirm}
					/>
				)}
			</Consents>
		</div>
	);
};

export default ServicesAggregationPage;
